import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Car, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ST = {
  valid: ["Validé", "bg-emerald-100 text-emerald-900"], renewal_soon: ["Renouvellement prochain", "bg-amber-100 text-amber-900"],
  submitted: ["En attente de vérification", "bg-violet-100 text-violet-900"], pending: ["En attente de vérification", "bg-violet-100 text-violet-900"],
  draft: ["Documents à envoyer", "bg-slate-100 text-slate-700"], rejected: ["Refusé", "bg-red-200 text-red-900"],
  expired: ["Assurance à renouveler", "bg-red-200 text-red-900"], awaiting_renewal: ["En attente de renouvellement", "bg-sky-100 text-sky-900"],
};
const KIND_FR = { car: "Voiture", motorcycle: "Moto", scooter: "Scooter" };

// Véhicules du conducteur : ajout (carte grise + assurance), validation admin, choix du véhicule utilisé.
export default function DriverVehiclesCard() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get("/price/vehicles/me").then(({ data: d }) => setData(d)).catch(() => setData({ items: [], max: 3 })), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e)); } };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin" />;

  return (
    <div className="space-y-2" data-testid="driver-vehicles-card">
      {data.items.map((v) => (
        <div key={v.id} className={`rounded-2xl bg-white p-3 street-border-thin ${v.active ? "ring-2 ring-primary" : ""}`} data-testid={`vehicle-${v.plate}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              <div>
                <div className="font-[Outfit] text-[15px] font-black">{v.brand_model}</div>
                <div className="text-[12px] font-semibold text-foreground/60">{v.plate} · {KIND_FR[v.kind] || "Véhicule"}{v.active ? " · utilisé actuellement" : ""}</div>
              </div>
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-black ${(ST[v.status] || ST.draft)[1]}`} data-testid={`vehicle-status-${v.plate}`}>{(ST[v.status] || ST.draft)[0]}</span>
          </div>
          {v.rejection_reason && v.status === "rejected" && <p className="mt-1 text-[12px] font-bold text-red-700">Motif : {v.rejection_reason}</p>}
          {!v.active && ["draft", "rejected", "expired"].includes(v.status) && <VehicleDocsForm v={v} onDone={load} />}
          {!v.active && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {v.status === "valid" && (
                <button type="button" onClick={() => act(() => api.post(`/price/vehicles/${v.id}/activate`), "Véhicule sélectionné")}
                  className="street-press inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[12px] font-black text-primary-foreground street-border-thin" data-testid={`vehicle-activate-${v.plate}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" />Utiliser ce véhicule
                </button>
              )}
              <button type="button" onClick={() => window.confirm("Supprimer ce véhicule ?") && act(() => api.delete(`/price/vehicles/${v.id}`), "Véhicule supprimé")}
                className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[12px] font-black text-red-600 street-border-thin" data-testid={`vehicle-delete-${v.plate}`}>
                <Trash2 className="h-3.5 w-3.5" />Supprimer
              </button>
            </div>
          )}
        </div>
      ))}
      <p className="text-[11px] font-semibold text-foreground/60">Un nouveau véhicule ne peut être utilisé qu&apos;après validation de sa carte grise et de son assurance par l&apos;équipe Price.</p>
      {data.items.length < data.max && (adding
        ? <AddVehicleForm onCancel={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />
        : (
          <button type="button" onClick={() => setAdding(true)} className="street-press flex h-11 w-full items-center justify-center gap-2 rounded-full bg-white font-[Outfit] text-[14px] font-black street-border-thin" data-testid="vehicle-add-open">
            <Plus className="h-4 w-4" />Ajouter un véhicule ({data.items.length}/{data.max})
          </button>
        ))}
    </div>
  );
}

function AddVehicleForm({ onCancel, onDone }) {
  const [f, setF] = useState({ brand_model: "", plate: "", kind: "car" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/price/vehicles", f); toast.success("Véhicule ajouté : envoie maintenant ses documents"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const input = "h-11 w-full rounded-xl bg-[#FFF8F1] px-3 text-[14px] font-semibold street-border-thin";
  return (
    <div className="space-y-2 rounded-2xl bg-white p-3 street-border-thin" data-testid="vehicle-add-form">
      <input className={input} placeholder="Marque et modèle (ex. Peugeot 208)" value={f.brand_model} onChange={(e) => setF({ ...f, brand_model: e.target.value })} data-testid="vehicle-add-brand" />
      <input className={input} placeholder="Plaque d'immatriculation" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} data-testid="vehicle-add-plate" />
      <select className={input} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="vehicle-add-kind">
        {Object.entries(KIND_FR).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="h-10 flex-1 rounded-full bg-white text-[13px] font-black street-border-thin" data-testid="vehicle-add-cancel">Annuler</button>
        <button type="button" disabled={busy} onClick={submit} className="street-press h-10 flex-1 rounded-full bg-primary text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-60" data-testid="vehicle-add-submit">
          {busy ? "…" : "Ajouter"}
        </button>
      </div>
    </div>
  );
}

function VehicleDocsForm({ v, onDone }) {
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await Promise.all(["carte_grise", "insurance"].filter((k) => files[k]).map((k) => {
        const fd = new FormData(); fd.append("kind", k); fd.append("file", files[k]);
        return api.post(`/price/vehicles/${v.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      }));
      await api.post(`/price/vehicles/${v.id}/submit`);
      toast.success("Documents envoyés : l'équipe Price va les vérifier");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); onDone(); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-1.5 rounded-xl bg-[#FFF8F1] p-2" data-testid={`vehicle-docs-${v.plate}`}>
      {[["carte_grise", "Carte grise"], ["insurance", "Justificatif d'assurance"]].map(([k, l]) => (
        <label key={k} className="flex items-center justify-between gap-2 text-[12px] font-bold">
          <span>{l}{v.files.includes(k) && !files[k] ? " ✓" : ""}</span>
          <input type="file" accept="image/*,application/pdf" onChange={(e) => setFiles({ ...files, [k]: e.target.files?.[0] })} className="max-w-[55%] text-[11px]" data-testid={`vehicle-file-${k}-${v.plate}`} />
        </label>
      ))}
      <button type="button" disabled={busy} onClick={send} className="street-press flex h-10 w-full items-center justify-center gap-2 rounded-full bg-primary text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-60" data-testid={`vehicle-submit-${v.plate}`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Envoyer pour vérification
      </button>
    </div>
  );
}
